import { gsap } from 'gsap';
import type { ThreeStage } from '../../../three/ThreeStage';
import { BaseScene } from '../../../core/BaseScene';
import { glyph } from '../../../components/glyphs';
import { researchMonogram } from '../../../components/brandLogo';
import { words, revealWords, settleWords } from '../../../components/text';

/** Card travel on the 1600×900 artboard: from beside the tool, into the review slot. */
const CARD = { startX: 0, reviewX: 640 };

/**
 * One draft through the space, in the Research Space interface language (PDF pp. 3–5, 9): white
 * cards on #E0E0E0, #37474F as the only filled control, status as a dot plus a label.
 *
 * 0 A research tool proposes a draft — Needs review
 * 1 The draft reaches a person — Review
 * 2 Approved — the version becomes the source of truth
 * 3 A later suggestion stops at the approved edge — Suggest, never overwrite.
 * 4 Humans approve the truth.
 */
export class ResearchApprovalScene extends BaseScene {
  public readonly stepCount = 5;

  protected template(): string {
    return `
      <div class="scene-layout rs-scene rs-approval">
        <div class="artboard">
          <div class="rs-context">
          <div class="rs-tool" aria-hidden="true">
            <span class="rs-tool__icon">${glyph('spark', 'glyph')}</span>
            <span><b>Research tool</b><small class="rs-mono">proposes</small></span>
          </div>
          <div class="rs-track" aria-hidden="true"></div>

          <section class="rs-card rs-panel" aria-label="Review">
            <header><span class="rs-eyebrow">Review</span><span class="rs-mono">1 awaiting a decision</span></header>
            <div class="rs-panel__slot"></div>
            <footer>
              <span class="rs-reviewer">${glyph('customer', 'glyph')}<span>Reviewer</span></span>
              <span class="rs-actions"><span class="rs-button rs-button--secondary">Edit</span><span class="rs-button rs-button--primary">Approve</span></span>
            </footer>
          </section>

          <div data-from="3">
            <article class="rs-card rs-suggestion" aria-label="Suggested edit">
              <span class="rs-tool__icon">${glyph('spark', 'glyph')}</span>
              <span><b>Suggested edit</b><span class="rs-badge rs-badge--review"><i></i>Awaiting approval</span></span>
            </article>
            <div class="rs-boundary" aria-hidden="true"></div>
          </div>
          </div>

          <article class="rs-card rs-note" aria-label="Draft note">
            <header><span class="rs-mono">D-14 · Note</span>${researchMonogram(22)}</header>
            <h3 class="rs-serif">Draft note</h3>
            <i class="rs-bar"></i><i class="rs-bar"></i><i class="rs-bar rs-bar--short"></i>
            <footer>
              <span class="rs-badge rs-badge--review"><i></i>Needs review</span>
              <span class="rs-badge rs-badge--approved"><i></i>Approved</span>
              <span class="rs-lock">${glyph('lock', 'glyph')}</span>
            </footer>
            <span class="rs-truth rs-eyebrow">Source of truth</span>
          </article>

          <div class="rs-statements">
            <h2 class="rs-statement" data-from="0" data-until="0">${words('Every draft starts as a proposal.')}</h2>
            <h2 class="rs-statement" data-from="1" data-until="1">${words('A person reviews it.')}</h2>
            <h2 class="rs-statement" data-from="2" data-until="2">${words('Approved work becomes the source of truth.')}</h2>
            <h2 class="rs-statement" data-from="3" data-until="3">${words('Suggest, never overwrite.')}</h2>
            <h2 class="rs-statement rs-statement--final" data-from="4">${words('Humans approve the truth.')}</h2>
          </div>
          <p class="rs-caption">Conceptual visualisation in the Research Space design language. Not a product screenshot.</p>
        </div>
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    if (immediate || this.step !== 0) return;
    const tl = this.timeline();
    tl.fromTo(this.$('.rs-tool'), { opacity: 0, x: -16 }, { opacity: 1, x: 0, duration: 0.7, ease: 'expo.out' }, 0.2);
    tl.fromTo(this.$('.rs-panel'), { opacity: 0 }, { opacity: 0.45, duration: 0.8 }, 0.3);
    tl.fromTo(this.$('.rs-note'), { opacity: 0, x: CARD.startX - 40 }, { opacity: 1, x: CARD.startX, duration: 0.9, ease: 'expo.out' }, 0.6);
    tl.add(revealWords(this.$('.rs-statement')), 1);
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode(this.meta.id, step, immediate);
    const forward = !immediate && step > previousStep;
    const d = immediate ? 0 : 1;
    const tl = this.timeline();

    const statements = this.$$('.rs-statement');
    statements.forEach((s) => settleWords(s));
    if (forward && step >= 1) tl.add(revealWords(statements[step]), step === 2 ? 1.2 : 0.4);

    const note = this.$('.rs-note');
    const panel = this.$('.rs-panel');
    const approve = this.$('.rs-button--primary');
    const approved = step >= 2;
    this.root.dataset.approved = String(approved);

    gsap.set(this.$('.rs-tool'), { opacity: step >= 1 ? 0.45 : 1, x: 0 });
    gsap.to(panel, { opacity: step === 0 ? 0.45 : 1, duration: d * 0.5 });
    panel.dataset.active = String(step === 1);

    // The draft travels from the tool into the review slot.
    if (forward && step === 1) {
      tl.to(note, { x: CARD.reviewX, duration: 1.1, ease: 'power3.inOut' }, 0.1);
    } else {
      gsap.set(note, { x: step >= 1 ? CARD.reviewX : CARD.startX, opacity: 1 });
    }

    // Approval: the single filled control is pressed; status changes from dot to dot.
    const reviewBadge = note.querySelector('.rs-badge--review')!;
    const approvedBadge = note.querySelector('.rs-badge--approved')!;
    const lock = note.querySelector('.rs-lock')!;
    const truth = note.querySelector('.rs-truth')!;
    if (forward && step === 2) {
      tl.to(approve, { scale: 0.94, duration: 0.14, yoyo: true, repeat: 1, ease: 'power2.inOut' }, 0.3);
      tl.to(reviewBadge, { opacity: 0, duration: 0.25 }, 0.6);
      tl.fromTo(approvedBadge, { opacity: 0, x: -6 }, { opacity: 1, x: 0, duration: 0.4, ease: 'power2.out' }, 0.7);
      tl.fromTo(lock, { opacity: 0, scale: 0.5 }, { opacity: 1, scale: 1, duration: 0.45, ease: 'back.out(2.4)' }, 0.85);
      tl.fromTo(truth, { opacity: 0, y: 6 }, { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out' }, 1);
    } else {
      gsap.set(reviewBadge, { opacity: approved ? 0 : 1 });
      gsap.set(approvedBadge, { opacity: approved ? 1 : 0, x: 0 });
      gsap.set(lock, { opacity: approved ? 1 : 0, scale: 1 });
      gsap.set(truth, { opacity: approved ? 1 : 0, y: 0 });
    }

    // A later suggestion arrives and holds at the approved edge.
    const suggestion = this.$('.rs-suggestion');
    if (forward && step === 3) {
      tl.fromTo(suggestion, { x: 160, y: -60, opacity: 0 }, { x: 0, y: 0, opacity: 1, duration: 1, ease: 'power3.out' }, 0.2);
      tl.fromTo(this.$('.rs-boundary'), { scaleY: 0 }, { scaleY: 1, duration: 0.6, ease: 'expo.out' }, 0.9);
    } else {
      gsap.set(suggestion, { x: 0, y: 0, opacity: 1 });
      gsap.set(this.$('.rs-boundary'), { scaleY: 1 });
    }

    // Final: only the approved truth stays at full strength.
    gsap.to(this.$('.rs-context'), { opacity: step >= 4 ? 0.3 : 1, duration: d * 0.6 });
  }
}
